import React, { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "./SalesPage.css";
import { getAuthToken, getCurrentShopId } from "./auth";

import {
  calculateBusinessSummary,
  getSalePaymentTotals,
  isRefundSale,
  isVoidSale,
  parseSaleItems,
} from "./reportCalculations";

const API_URL = process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

const REPORT_TIME_ZONE = "Europe/Dublin";

const getTodayDate = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-IE", {
    timeZone: REPORT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const part = (type) => parts.find((value) => value.type === type).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
};

const formatPdfMoney = (value) => {
  return `EUR ${Number(value || 0).toFixed(2)}`;
};

const formatPdfPercent = (value) => {
  return `${Number(value || 0).toFixed(1)}%`;
};

const formatPdfDate = (value) => {
  if (!value) return "-";

  try {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return "-";

    return date.toLocaleString("en-IE", {
      timeZone: REPORT_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).replace(", ", " ");
  } catch {
    return "-";
  }
};

const truncateText = (value, maxLength = 28) => {
  const text = String(value ?? "-");

  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength - 3)}...`;
};

function SalesPage() {
  const today = getTodayDate();

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({
    grossSales: 0,
    refundTotal: 0,
    netSales: 0,

    cashSales: 0,
    cashReturns: 0,
    netCash: 0,

    cardSales: 0,
    cardReturns: 0,
    netCard: 0,

    saleTransactions: 0,
    refundTransactions: 0,
    totalTransactions: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shopId, setShopId] = useState(null);
  const [shopName, setShopName] = useState("");

  const [tills, setTills] = useState([]);
  const [selectedTillId, setSelectedTillId] = useState("all");
  const [tillsLoading, setTillsLoading] = useState(false);

  const loadShopName = async (activeShopId) => {
  try {
    if (!activeShopId) return;

    const token = getAuthToken();

    const response = await axios.get(
      `${API_URL}/shop-settings`,
      {
        params: {
          shop_id: activeShopId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setShopName(
      response.data?.shop_table_name ||
        response.data?.shop_name ||
        response.data?.name ||
        ""
    );
  } catch (err) {
    console.error(
      "Sales page shop name error:",
      err.response?.data || err.message
    );

    setShopName("");
  }
};

  useEffect(() => {
    const currentShopId = getCurrentShopId();

    console.log("SalesPage current admin shop id:", currentShopId);

    setShopId(currentShopId);
    loadTills(currentShopId);
    loadShopName(currentShopId);
  }, []);

  const loadTills = async (activeShopId) => {
    try {
      if (!activeShopId) return;

      setTillsLoading(true);

      const token = getAuthToken();

      const response = await axios.get(`${API_URL}/tills`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const tillList = Array.isArray(response.data?.tills)
        ? response.data.tills
        : [];

      setTills(tillList);
    } catch (err) {
      console.error(
        "Load report tills error:",
        err.response?.data || err.message,
      );

      setTills([]);
    } finally {
      setTillsLoading(false);
    }
  };

  const getSaleProfitTotals = (sale) => {
    if (isVoidSale(sale) || isRefundSale(sale)) {
      return {
        salesTotal: 0,
        costTotal: 0,
        profitTotal: 0,
        margin: 0,
      };
    }

    const items = parseSaleItems(sale);

    let salesTotal = 0;
    let costTotal = 0;
    let profitTotal = 0;

    for (const item of items) {
      const qty = Number(item.qty || item.quantity || 1);

      const price = Number(item.price || 0);

      const costPrice = Number(
        item.cost_price ||
          item.costPrice ||
          item.cost ||
          item.buying_price ||
          item.buyingPrice ||
          0,
      );

      const itemSales = price * qty;
      const itemCost = costPrice * qty;

      salesTotal += itemSales;
      costTotal += itemCost;
      profitTotal += itemSales - itemCost;
    }

    const margin = salesTotal > 0 ? (profitTotal / salesTotal) * 100 : 0;

    return {
      salesTotal,
      costTotal,
      profitTotal,
      margin,
    };
  };

  const calculateProfitSummary = (salesList = []) => {
    return salesList.reduce(
      (profitSummary, sale) => {
        const totals = getSaleProfitTotals(sale);

        profitSummary.salesTotal += totals.salesTotal;
        profitSummary.costTotal += totals.costTotal;
        profitSummary.profitTotal += totals.profitTotal;

        return profitSummary;
      },
      {
        salesTotal: 0,
        costTotal: 0,
        profitTotal: 0,
      },
    );
  };

  const loadSales = async () => {
    try {
      if (!shopId) {
        setError("Shop is still loading. Please wait.");
        return;
      }

      setLoading(true);
      setError("");

     const token = getAuthToken();

const res = await axios.get(`${API_URL}/sales`, {
  params: {
    shop_id: shopId,
    from: fromDate,
    to: toDate,
    till_id:
      selectedTillId === "all"
        ? "all"
        : Number(selectedTillId),
  },
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

      console.log("SALES FOR SHOP:", shopId);
      console.log("SALES DATA:", res.data);

      const salesList = Array.isArray(res.data.sales) ? res.data.sales : [];

      setSales(salesList);

      const businessSummary = calculateBusinessSummary(salesList);

      console.log("SALES BUSINESS SUMMARY:", businessSummary);

      setSummary(businessSummary);
    } catch (err) {
      console.error("Load sales error:", err.response?.data || err.message);
      setError(err.response?.data?.error || "Failed to load sales");
    } finally {
      setLoading(false);
    }
  };

  const getItemsCount = (sale) => {
    try {
      const items =
        typeof sale.items === "string" ? JSON.parse(sale.items) : sale.items;

      if (!Array.isArray(items)) return 0;

      return items.reduce((sum, item) => sum + Number(item.qty || 0), 0);
    } catch {
      return 0;
    }
  };

  const getProductNames = (sale) => {
    try {
      const items =
        typeof sale.items === "string" ? JSON.parse(sale.items) : sale.items;

      if (!Array.isArray(items)) return "-";

      return items
        .map((item) => {
          const name = item.name || item.product_name || "Unknown Product";
          const qty = item.qty || 1;

          return `${name} x${qty}`;
        })
        .join(", ");
    } catch {
      return "-";
    }
  };

  const profitSummary = calculateProfitSummary(sales);

  const profitMargin =
    profitSummary.salesTotal > 0
      ? (profitSummary.profitTotal / profitSummary.salesTotal) * 100
      : 0;

  const createSalesReportPdf = (action = "download") => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 8;
    let y = 12;

    const addPageIfNeeded = (requiredSpace = 10) => {
      if (y + requiredSpace > pageHeight - 10) {
        doc.addPage();
        y = 12;
      }
    };

    const drawLine = () => {
      doc.setDrawColor(180);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;
    };

    const drawTableHeader = (columns, widths) => {
      addPageIfNeeded(12);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);

      let x = margin;

      columns.forEach((column, index) => {
        doc.text(column, x, y);
        x += widths[index];
      });

      y += 4;
      doc.setDrawColor(190);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;
    };

    const drawTableRow = (cells, widths) => {
      addPageIfNeeded(8);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);

      let x = margin;

      cells.forEach((cell, index) => {
        doc.text(String(cell), x, y);
        x += widths[index];
      });

      y += 5;
    };

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text("Sales Report", margin, y);

    y += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
  `Shop: ${shopName || `Shop ID ${shopId || "-"}`}`,
  margin,
  y
);
    doc.text(
      `Printed: ${formatPdfDate(new Date())} (Ireland)`,
      pageWidth - margin,
      y,
      { align: "right" },
    );

    y += 5;

    doc.text(`From: ${fromDate}`, margin, y);
    doc.text(`To: ${toDate}`, margin + 45, y);

    const selectedTill =
      selectedTillId === "all"
        ? null
        : tills.find((till) => String(till.id) === String(selectedTillId));

    doc.text(
      `Till: ${
        selectedTill
          ? `${selectedTill.name} (${selectedTill.till_code})`
          : "All Tills"
      }`,
      margin + 90,
      y,
    );

    y += 7;
    drawLine();

    // Summary
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Summary", margin, y);

    y += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    doc.text(`Sales Transactions: ${summary.saleTransactions}`, margin, y);

    doc.text(
      `Refund Transactions: ${summary.refundTransactions}`,
      margin + 65,
      y,
    );

    doc.text(
      `Gross Sales: ${formatPdfMoney(summary.grossSales)}`,
      margin + 130,
      y,
    );

    doc.text(
      `Refunds: ${formatPdfMoney(summary.refundTotal)}`,
      margin + 195,
      y,
    );

    y += 6;

    doc.text(`Net Sales: ${formatPdfMoney(summary.netSales)}`, margin, y);

    doc.text(`Net Cash: ${formatPdfMoney(summary.netCash)}`, margin + 65, y);

    doc.text(`Net Card: ${formatPdfMoney(summary.netCard)}`, margin + 130, y);

    doc.text(
      `Gross Profit: ${formatPdfMoney(profitSummary.profitTotal)}`,
      margin + 195,
      y,
    );

    y += 6;

    doc.text(
      `Total Cost: ${formatPdfMoney(profitSummary.costTotal)}`,
      margin,
      y,
    );

    doc.text(
      `Profit Margin: ${formatPdfPercent(profitMargin)}`,
      margin + 65,
      y,
    );

    y += 8;
    drawLine();

    // Sales List
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Sales List", margin, y);

    y += 7;

    const columns = [
      "Sale",
      "Date",
      "Till",
      "Cashier",
      "Products",
      "Qty",
      "Total",
      "Cost",
      "Profit",
      "Cash",
      "Card",
      "Method",
    ];

    const widths = [16, 35, 27, 28, 52, 10, 22, 22, 22, 20, 20, 18];

    drawTableHeader(columns, widths);

    if (!sales || sales.length === 0) {
      drawTableRow(
        ["No sales found", "", "", "", "", "", "", "", "", "", "", ""],
        widths,
      );
    } else {
      sales.forEach((sale) => {
        const payment = getSalePaymentTotals(sale);
        const profit = getSaleProfitTotals(sale);

        drawTableRow(
          [
            `#${sale.id}`,
            truncateText(formatPdfDate(sale.created_at), 19),
            truncateText(sale.till_name || "Unknown", 14),
            truncateText(sale.user_name || "Unknown", 14),
            truncateText(getProductNames(sale), 28),
            getItemsCount(sale),
            formatPdfMoney(sale.total),
            formatPdfMoney(profit.costTotal),
            formatPdfMoney(profit.profitTotal),
            formatPdfMoney(payment.cash),
            formatPdfMoney(payment.card),
            truncateText(sale.payment_method || "split", 8),
          ],
          widths,
        );
      });
    }

    // Footer page numbers
    const totalPages = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i += 1) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth - margin - 25,
        pageHeight - 6,
      );
    }

    const tillFileLabel =
      selectedTillId === "all" ? "all-tills" : `till-${selectedTillId}`;

   const safeShopName = String(
  shopName || `shop-${shopId || "unknown"}`
)
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

const fileName =
  `sales-report-${safeShopName}-${tillFileLabel}-${fromDate}-to-${toDate}.pdf`;

    if (action === "print") {
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, "_blank");

      if (printWindow) {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 700);
      } else {
        doc.save(fileName);
      }

      return;
    }

    doc.save(fileName);
  };

  const selectedTillLabel =
    selectedTillId === "all"
      ? "All Tills"
      : tills.find((till) => String(till.id) === String(selectedTillId))
          ?.name || "Selected Till";

  return (
    <div className="sales-page">
     <div className="sales-header">
  <div>
    <span className="sales-header-label">
      Reports
    </span>

    <h1>Sales Report</h1>

    <p>
      Review sales, payments, refunds and profit for the
      selected period.
    </p>
  </div>

  <div className="sales-badge">
    {selectedTillLabel}
  </div>
</div>

<div className="sales-filter-card">
  <div className="sales-filter-heading">
    <div>
      <h2>Report Filters</h2>
      <p>Select the dates and till you want to review.</p>
    </div>
  </div>

  <div className="sales-filter">
    <label>
      <span>From Date</span>

      <input
        type="date"
        value={fromDate}
        onChange={(e) => setFromDate(e.target.value)}
      />
    </label>

    <label>
      <span>To Date</span>

      <input
        type="date"
        value={toDate}
        onChange={(e) => setToDate(e.target.value)}
      />
    </label>

    <label>
      <span>Till</span>

      <select
        value={selectedTillId}
        onChange={(e) =>
          setSelectedTillId(e.target.value)
        }
        disabled={tillsLoading}
        className="sales-till-select"
      >
        <option value="all">All Tills</option>

        {tills.map((till) => (
          <option
            key={till.id}
            value={String(till.id)}
          >
            {till.name} ({till.till_code})
          </option>
        ))}
      </select>
    </label>

    <div className="sales-filter-actions">
      <button
        type="button"
        className="sales-primary-btn"
        onClick={loadSales}
        disabled={loading}
      >
        {loading ? "Loading..." : "Load Sales"}
      </button>

      <button
        type="button"
        className="sales-secondary-btn"
        onClick={() =>
          createSalesReportPdf("download")
        }
        disabled={loading}
      >
        Export PDF
      </button>

      <button
        type="button"
        className="sales-secondary-btn"
        onClick={() =>
          createSalesReportPdf("print")
        }
        disabled={loading}
      >
        Print PDF
      </button>
    </div>
  </div>
</div>

{error && (
  <div className="sales-error">
    {error}
  </div>
)}

<div className="sales-primary-summary">
  <div className="sales-kpi-card sales-kpi-net">
    <span>Net Sales</span>

    <strong>
      €{Number(summary.netSales || 0).toFixed(2)}
    </strong>

    <small>Sales after refunds</small>
  </div>

  <div className="sales-kpi-card">
    <span>Gross Sales</span>

    <strong>
      €{Number(summary.grossSales || 0).toFixed(2)}
    </strong>

    <small>Sales before refunds</small>
  </div>

  <div className="sales-kpi-card sales-kpi-refund">
    <span>Refunds</span>

    <strong>
      €{Number(summary.refundTotal || 0).toFixed(2)}
    </strong>

    <small>
      {summary.refundTransactions || 0} refund transactions
    </small>
  </div>

  <div className="sales-kpi-card">
    <span>Total Activity</span>

    <strong>
      {summary.totalTransactions || 0}
    </strong>

    <small>
      {summary.saleTransactions || 0} completed sales
    </small>
  </div>
</div>

<div className="sales-breakdown-grid">
  <section className="sales-breakdown-panel">
    <div className="sales-section-heading">
      <span>Payments</span>
      <h2>Cash</h2>
    </div>

    <div className="sales-breakdown-row">
      <span>Cash Sales</span>
      <strong>
        €{Number(summary.cashSales || 0).toFixed(2)}
      </strong>
    </div>

    <div className="sales-breakdown-row">
      <span>Cash Refunds</span>
      <strong className="sales-negative-value">
        €{Number(summary.cashReturns || 0).toFixed(2)}
      </strong>
    </div>

    <div className="sales-breakdown-total">
      <span>Net Cash</span>
      <strong>
        €{Number(summary.netCash || 0).toFixed(2)}
      </strong>
    </div>
  </section>

  <section className="sales-breakdown-panel">
    <div className="sales-section-heading">
      <span>Payments</span>
      <h2>Card</h2>
    </div>

    <div className="sales-breakdown-row">
      <span>Card Sales</span>
      <strong>
        €{Number(summary.cardSales || 0).toFixed(2)}
      </strong>
    </div>

    <div className="sales-breakdown-row">
      <span>Card Refunds</span>
      <strong className="sales-negative-value">
        €{Number(summary.cardReturns || 0).toFixed(2)}
      </strong>
    </div>

    <div className="sales-breakdown-total">
      <span>Net Card</span>
      <strong>
        €{Number(summary.netCard || 0).toFixed(2)}
      </strong>
    </div>
  </section>

  <section className="sales-breakdown-panel">
    <div className="sales-section-heading">
      <span>Performance</span>
      <h2>Profit</h2>
    </div>

    <div className="sales-breakdown-row">
      <span>Total Cost</span>
      <strong>
        €
        {Number(
          profitSummary.costTotal || 0
        ).toFixed(2)}
      </strong>
    </div>

    <div className="sales-breakdown-row">
      <span>Gross Profit</span>
      <strong className="sales-positive-value">
        €
        {Number(
          profitSummary.profitTotal || 0
        ).toFixed(2)}
      </strong>
    </div>

    <div className="sales-breakdown-total">
      <span>Profit Margin</span>
      <strong>
        {Number(profitMargin || 0).toFixed(1)}%
      </strong>
    </div>
  </section>
</div>

      <div className="sales-table-card">
        <div className="sales-table-heading">
  <div>
    <span>Transactions</span>
    <h2>Sales List</h2>
  </div>

  <strong>
    {sales.length} record{sales.length === 1 ? "" : "s"}
  </strong>
</div>

        <div className="sales-table-wrap">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Sale ID</th>
                <th>Date</th>
                <th>Till</th>
                <th>Cashier</th>
                <th>Products</th>
                <th>Items</th>
                <th>Total</th>
                <th>Cost</th>
                <th>Profit</th>
                <th>Margin</th>
                <th>Cash</th>
                <th>Card</th>
                <th>Payment</th>
              </tr>
            </thead>

            <tbody>
              {sales.length === 0 ? (
                <tr>
                  <td colSpan="13" className="no-sales">
                    No sales found for selected dates.
                  </td>
                </tr>
              ) : (
                sales.map((sale) => {
                  const payment = getSalePaymentTotals(sale);
                  const profit = getSaleProfitTotals(sale);

                  return (
                    <tr key={sale.id}>
                      <td>#{sale.id}</td>

                      <td>
                        {formatPdfDate(sale.created_at)}
                      </td>

                      <td>
                        <strong>{sale.till_name || "Legacy / Unknown"}</strong>

                        {sale.till_code && (
                          <div className="sales-cell-subtext">
                            {sale.till_code}
                          </div>
                        )}
                      </td>

                      <td>
                        <strong>{sale.user_name || "Unknown"}</strong>

                        {sale.user_email && (
                          <div className="sales-cell-subtext">
                            {sale.user_email}
                          </div>
                        )}
                      </td>

                      <td className="product-names-cell">
                        {getProductNames(sale)}
                      </td>

                      <td>{getItemsCount(sale)}</td>

                      <td>€{Number(sale.total || 0).toFixed(2)}</td>

                      <td>€{Number(profit.costTotal || 0).toFixed(2)}</td>

                      <td>€{Number(profit.profitTotal || 0).toFixed(2)}</td>

                      <td>{Number(profit.margin || 0).toFixed(1)}%</td>

                      <td>€{Number(payment.cash || 0).toFixed(2)}</td>

                      <td>€{Number(payment.card || 0).toFixed(2)}</td>

                      <td>{sale.payment_method || "split"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default SalesPage;
